import { useState, useEffect, useCallback } from 'react';

interface UseSideNavCollapseOptions {
  defaultCollapsed?: boolean;
  persistState?: boolean;
  storageKey?: string;
  breakpoint?: number; // Screen width below which nav auto-collapses
}

interface UseSideNavCollapseReturn {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;
  isAutoCollapsed: boolean; // True when collapsed due to screen size
}

export function useSideNavCollapse({
  defaultCollapsed = false,
  persistState = true,
  storageKey = 'sidenav-collapsed',
  breakpoint = 1024, // lg breakpoint
}: UseSideNavCollapseOptions = {}): UseSideNavCollapseReturn {
  const [collapsed, setCollapsedState] = useState(defaultCollapsed);
  const [isAutoCollapsed, setIsAutoCollapsed] = useState(false);

  // Load initial state from localStorage
  useEffect(() => {
    if (!persistState) return;

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored !== null) {
        setCollapsedState(JSON.parse(stored));
      }
    } catch (error) {
      console.warn('Failed to load sidenav state from localStorage:', error);
    }
  }, [persistState, storageKey]);

  // Handle responsive behavior
  useEffect(() => {
    if (!breakpoint) return;

    const handleResize = () => {
      const isMobile = window.innerWidth < 640;
      const shouldAutoCollapse = window.innerWidth < breakpoint;
      // Only mark auto-collapsed when the sidebar is actually collapsed by layout
      // i.e. between mobile overlay and large screens
      setIsAutoCollapsed(shouldAutoCollapse && !isMobile);

      if (shouldAutoCollapse && !isMobile) {
        // For tablet/small desktop: force collapse
        setCollapsedState(true);
      } else if (!shouldAutoCollapse) {
        // For large screens: restore user preference
        if (persistState) {
          try {
            const stored = localStorage.getItem(storageKey);
            if (stored !== null) {
              setCollapsedState(JSON.parse(stored));
            }
          } catch (error) {
            console.warn('Failed to restore sidenav state:', error);
          }
        }
      } else if (isMobile) {
        // For mobile: always show full sidebar (not collapsed)
        setCollapsedState(false);
      }
    };

    // Check initial size
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint, persistState, storageKey]);

  const setCollapsed = useCallback(
    (newCollapsed: boolean) => {
      setCollapsedState(newCollapsed);

      // Only persist user-initiated changes, not auto-collapses
      if (persistState && !isAutoCollapsed) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(newCollapsed));
        } catch (error) {
          console.warn('Failed to save sidenav state to localStorage:', error);
        }
      }
    },
    [persistState, storageKey, isAutoCollapsed],
  );

  const toggleCollapsed = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed, setCollapsed]);

  return {
    collapsed,
    setCollapsed,
    toggleCollapsed,
    isAutoCollapsed,
  };
}
